import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TenantGroupAccessService } from '../../../services/tenant-group-access.service';
import { ToastService } from '../../../services/toast-service';
import { GrantAccessModalComponent } from './grant-access-modal/grant-access-modal.component';
import { AppSettings } from '../../../shared/app-settings';

@Component({
  selector: 'app-employee-tenant-access',
  templateUrl: './employee-tenant-access.component.html',
  styleUrls: ['./employee-tenant-access.component.scss']
})
export class EmployeeTenantAccessComponent implements OnInit {
  @ViewChild('revokeConfirmModal') revokeConfirmModal!: TemplateRef<any>;
  
  employeeAccess: any = { rows: [], totalCount: 0 };
  columns: any[] = [];
  loading = false;
  isUserAdmin = false;
  itemToRevoke: any = null;
  private modalRef?: NgbModalRef;

  constructor(
    private accessService: TenantGroupAccessService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadUserAdminState();
    this.setupColumns();
    this.loadEmployeeAccess();
  }

  loadUserAdminState(): void {
    // Match the storage convention used by news.component / login flow:
    // canonical key is AppSettings.StorageKeys.User (= 'user'); the legacy
    // 'userData' key is kept as a fallback for older sessions.
    const rawUserData = localStorage.getItem(AppSettings.StorageKeys.User)
      || localStorage.getItem('userData');
    if (!rawUserData) {
      this.isUserAdmin = false;
      return;
    }

    try {
      const parsed = JSON.parse(rawUserData);
      this.isUserAdmin = parsed?.userAdmin === '1' || parsed?.userAdmin === 1 || parsed?.userAdmin === true;
    } catch {
      this.isUserAdmin = false;
    }
  }

  setupColumns(): void {
    this.columns = [
      {
        field: 'accessID',
        title: this.translateService.instant('EmployeeTenantAccess.employeeID'),
        width: 100,
        hidden: true
      },
      {
        field: 'employeeName',
        title: this.translateService.instant('EmployeeTenantAccess.employeeName'),
        width: 200
      },
      {
        field: 'groupName',
        title: this.translateService.instant('EmployeeTenantAccess.groupName'),
        width: 200
      },
      {
        field: 'grantedDate',
        title: this.translateService.instant('EmployeeTenantAccess.grantedDate'),
        width: 150,
        type: 'date'
      },
      {
        field: 'grantedByUserName',
        title: this.translateService.instant('EmployeeTenantAccess.grantedBy'),
        width: 150
      },
      {
        field: '__actions__',
        title: this.translateService.instant('General.actions'),
        width: 100,
        filterable: false,
        sortable: false
      }
    ];
  }

  loadEmployeeAccess(): void {
    this.loading = true;
    this.accessService.getEmployeeAccess().subscribe({
      next: (response) => {
        if (response?.success) {
          this.employeeAccess = {
            rows: response.result || [],
            totalCount: response.result?.length || 0
          };
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading employee access:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('EmployeeTenantAccess.loadFailed')]
        });
        this.loading = false;
      }
    });
  }

  openGrantAccessModal(): void {
    if (!this.isUserAdmin) return;

    const modalRef = this.modalService.open(GrantAccessModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });

    modalRef.result.then(
      (result) => {
        if (result === 'granted') {
          this.loadEmployeeAccess();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('EmployeeTenantAccess.grantSuccess')]
          });
        }
      },
      () => {} // Modal dismissed
    );
  }

  revokeAccess(row: any): void {
    if (!this.isUserAdmin) return;

    this.itemToRevoke = row;
    this.modalRef = this.modalService.open(this.revokeConfirmModal, {
      centered: true,
      backdrop: 'static'
    });
  }

  confirmRevoke(): void {
    if (!this.itemToRevoke) return;

    this.accessService.revokeAccess(this.itemToRevoke.accessID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.loadEmployeeAccess();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('EmployeeTenantAccess.revokeSuccess')]
          });
        } else {
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('EmployeeTenantAccess.revokeFailed')]
          });
        }
        this.modalRef?.close();
        this.itemToRevoke = null;
      },
      error: (error) => {
        console.error('Error revoking access:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('EmployeeTenantAccess.revokeFailed')]
        });
        this.modalRef?.close();
        this.itemToRevoke = null;
      }
    });
  }

  cancelRevoke(): void {
    this.modalRef?.close();
    this.itemToRevoke = null;
  }
}
